import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ModelPreviewPanel } from '@spazious/dollhouse-engine';
import { VirtualObjectType } from '@spazious/dollhouse-graphql';
import { useGetVirtualObjectsSuspenseQuery } from '@spazious/dollhouse-graphql/apollo';
import { Translate } from '@spazious/dollhouse-i18n';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStore } from '@spazious/dollhouse-store';
import { styled } from '@spazious/dollhouse-theme';
import {
  Flex,
  LinkButton,
  Panel,
  PanelContent,
  PanelHeader,
  PanelProps,
  Typography,
} from '@spazious/dollhouse-uikit';

import { DraggableCard, DraggableCardOverlay } from '../DraggableCard/index.js';
import { ModelPreviewCard } from '../ModelPreviewCard/index.js';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 2fr));
  grid-gap: calc(2 * var(--spacing-padding));
  grid-auto-rows: 1fr;
`;

export interface DecorationsPanelProps extends PanelProps {}

export const DecorationsPanel: React.FC<DecorationsPanelProps> = () => {
  const ready = useStore((state) => state.status === 'ready');
  const spaceId = useGraph().graph.shallow.space;
  const previewType: 'image' | 'canvas' = 'image';
  const pageNumberRef = useRef(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { data, fetchMore } = useGetVirtualObjectsSuspenseQuery({
    skip: !ready || !spaceId,
    variables: {
      spaceId: `${spaceId}`,
      type: VirtualObjectType.Decoration,
    },
  });

  const handleLoadMore = useCallback(async () => {
    if (!fetchMore) {
      setHasMore(false);
      return;
    }

    setIsLoading(true);
    try {
      pageNumberRef.current += 1;
      await fetchMore({
        variables: {
          pageNumber: pageNumberRef.current,
        },
      });
    } catch (error) {
      console.error('Error loading more decorations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchMore]);

  useEffect(() => {
    setHasMore(data?.virtualObjects.pagination?.hasNext ?? false);
  }, [data]);

  return (
    <Panel>
      <PanelHeader>
        <Flex column gap="var(--spacing-padding)">
          <Typography size="small" variant="headline">
            <Translate>Decorations</Translate>
          </Typography>
          <Flex alignItems="center">
            <Typography size="small" variant="title">
              <Translate>Drag and drop a decoration to the scene.</Translate>
            </Typography>
          </Flex>
        </Flex>
      </PanelHeader>
      <PanelContent style={{ position: 'relative' }}>
        <ModelPreviewPanel type={previewType}>
          <Grid>
            {(data?.virtualObjects.data ?? []).map(({ id, nid, model, thumbnail, name }, idx) => (
              <DraggableCard
                key={`${id}-decorations-${idx}`}
                node={{
                  type: FShallowNodeTypesEnum.Decoration,
                  id: nid,
                }}
              >
                <ModelPreviewCard
                  name={name}
                  thumbnail={thumbnail}
                  type={previewType}
                  url={model}
                />
              </DraggableCard>
            ))}
          </Grid>
          {hasMore && (
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{ padding: 'var(--spacing-padding)' }}
            >
              <LinkButton onClick={handleLoadMore}>
                {isLoading ? <Translate>Loading...</Translate> : <Translate>Load More</Translate>}
              </LinkButton>
            </Flex>
          )}
        </ModelPreviewPanel>
        <DraggableCardOverlay />
      </PanelContent>
    </Panel>
  );
};
